import { ReactNode } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { PlaylistAddCheck as LinkIcon } from "@mui/icons-material";

import useResponsive from "hooks/useResponsive";
import type { PdfJobDetail } from "features/pdfTools/pdfTools";
import { deriveSplitSsnState, splitSsnLinkable, splitSsnReasons } from "./splitSsn";

export interface SplitSsnCheckAlertProps {
  job: Pick<PdfJobDetail, "status" | "publish" | "discarded_at" | "ssn_check">;
  vendor: string;
  canEdit: boolean;
  readOnlyReason: string | null;
  rechecking: boolean;
  onRecheck: () => void;
  onOpenUpdater: () => void;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function plural(count: number, word: string): string {
  return `${count.toLocaleString()} ${word}${count === 1 ? "" : "s"}`;
}

/**
 * CAL-5987: the saved employee folders against the vendor's SSN file. An
 * upload job matches a "<name> - <last 4 digits>" folder only once SSN Updater
 * has filed it under its SSN, so the split job says how many it can file and
 * why the others cannot - before anyone creates the upload job.
 */
export default function SplitSsnCheckAlert({
  job,
  vendor,
  canEdit,
  readOnlyReason,
  rechecking,
  onRecheck,
  onOpenUpdater,
}: SplitSsnCheckAlertProps) {
  const { isCardMode } = useResponsive();
  const state = deriveSplitSsnState(job);
  const check = job.ssn_check;
  const counts = splitSsnLinkable(check);
  const reasons = splitSsnReasons(check);
  if (state === "hidden") return null;

  const writeReason = !canEdit ? (readOnlyReason ?? "Read-only access.") : "";
  const recheckButton = (
    <Tooltip title={writeReason}>
      <span>
        <Button
          size="small"
          color="inherit"
          disabled={!canEdit || rechecking || state === "checking"}
          onClick={onRecheck}
          startIcon={rechecking ? <CircularProgress size={12} color="inherit" /> : undefined}
        >
          {check ? "Re-check" : "Check now"}
        </Button>
      </span>
    </Tooltip>
  );
  const updaterButton = (primary: boolean) => (
    <Button
      size="small"
      color="inherit"
      variant={primary ? "outlined" : "text"}
      startIcon={<LinkIcon fontSize="small" />}
      onClick={onOpenUpdater}
    >
      SSN Updater
    </Button>
  );
  const frame = (
    severity: "info" | "success" | "warning" | "error",
    title: ReactNode,
    body: ReactNode,
    actions: ReactNode,
    icon?: ReactNode,
  ) => (
    <Alert
      severity={severity}
      icon={icon}
      sx={{ "& .MuiAlert-message": { minWidth: 0, flex: 1 } }}
      action={isCardMode ? undefined : actions}
    >
      <AlertTitle sx={{ fontWeight: 700, mb: body ? 0.5 : 0 }}>{title}</AlertTitle>
      {body}
      {isCardMode && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
          {actions}
        </Stack>
      )}
    </Alert>
  );
  const checked = check?.checked_at ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      Checked {formatWhen(check.checked_at)}.
    </Typography>
  ) : null;
  const reasonLines =
    reasons.length > 0 ? (
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {reasons.map((reason) => (
          <Typography component="li" variant="body2" key={reason.label}>
            {plural(reason.count, "folder")}: {reason.label}
          </Typography>
        ))}
      </Box>
    ) : null;
  const lastRun = check?.ssn_updater;
  const lastRunLine =
    lastRun && lastRun.state !== "running" ? (
      <Typography variant="body2">
        {lastRun.state === "done"
          ? `SSN Updater filed ${plural(lastRun.added ?? 0, "folder name")} ${formatWhen(lastRun.finished_at)}.`
          : lastRun.state === "interrupted"
            ? "The last SSN Updater run stopped reporting - run it again."
            : `The last SSN Updater run failed: ${lastRun.error ?? "unknown error"}.`}
      </Typography>
    ) : null;

  switch (state) {
    case "not_saved":
      return (
        <Typography variant="body2" color="text.secondary">
          Once the result is saved, its employee folders are checked against the {vendor}{" "}
          SSN file - upload jobs match them by it.
        </Typography>
      );
    case "waiting":
      return null;
    case "not_checked":
      return frame(
        "info",
        `The saved folders have not been checked against the ${vendor} SSN file yet.`,
        null,
        recheckButton,
      );
    case "checking":
      return frame(
        "info",
        `Checking the saved folders against the ${vendor} SSN file...`,
        null,
        null,
        <CircularProgress size={18} />,
      );
    case "updater_running":
      return frame(
        "info",
        `SSN Updater is filing the folders under their SSNs in the ${vendor} SSN file...`,
        null,
        updaterButton(false),
        <CircularProgress size={18} />,
      );
    case "error":
      return frame(
        "warning",
        "The SSN check did not finish.",
        <Typography variant="body2">{check?.error ?? "Unknown error."}</Typography>,
        recheckButton,
      );
    case "ssn_file_missing":
      // Missing or empty alike. The file is the vendor's: the SSN Job of the
      // client's Download major job collects it. Linking waits for it; the split
      // itself needs nothing more.
      return frame(
        "info",
        `The ${vendor} SSN file of this client is not ready, so the folders cannot be linked yet.`,
        <>
          <Typography variant="body2">
            The SSN Job of a {vendor} Download major job collects it; check again once it
            has run.
          </Typography>
          {checked}
        </>,
        recheckButton,
      );
    case "blocked":
      return frame(
        "warning",
        `${plural(counts.total, "folder")} could be filed under their SSNs, but SSN Updater does not write to the ${vendor} SSN file.`,
        <>
          <Typography variant="body2">{check?.updater_blocked?.message}</Typography>
          {checked}
        </>,
        recheckButton,
      );
    case "linkable":
      return frame(
        "warning",
        `${counts.total.toLocaleString()} of ${plural(counts.folders, "employee folder")} can be linked to the ${vendor} SSN file.`,
        <>
          <Typography variant="body2">
            {counts.exact.toLocaleString()} by exact name, {counts.similar.toLocaleString()} by
            similar name
            {counts.alreadyLinked > 0
              ? `, ${counts.alreadyLinked.toLocaleString()} already in the file`
              : ""}
            . An upload job reading these folders matches them only once they are linked.
          </Typography>
          {reasonLines}
          {lastRunLine}
          {checked}
        </>,
        <>
          <Tooltip title={writeReason}>
            <span>{updaterButton(true)}</span>
          </Tooltip>
          {recheckButton}
        </>,
      );
    case "all_linked":
      return frame(
        "success",
        `The saved folders are linked to the ${vendor} SSN file (${plural(counts.alreadyLinked, "folder")}).`,
        <>
          {reasonLines}
          {lastRunLine}
          {checked}
        </>,
        <>
          {updaterButton(false)}
          {recheckButton}
        </>,
      );
    case "none_linkable":
    default:
      return frame(
        "info",
        `None of the ${plural(counts.folders, "employee folder")} can be linked to the ${vendor} SSN file.`,
        <>
          {reasonLines}
          {lastRunLine}
          {checked}
        </>,
        <>
          {updaterButton(false)}
          {recheckButton}
        </>,
      );
  }
}
